import { IBuildings } from '@llws/lift-entity-interfaces';
import { ICityPageRegionAssignmentChange, ICityPageRegionProperty } from '../types/city-page-regions';
import { subsetObject } from './subset-object';

/**
 * Relation keys stripped from a property before it is PUT back.
 *
 * Mirrors the omit list in `mixins/property-mixin.ts` and the `properties/saveV2` action. Kept here
 * rather than shared with them: promoting it would mean touching both of those save paths, which this
 * feature has no reason to disturb.
 */
const propertyRelationKeys: string[] = [
  'amenities',
  'buildingsHasPhotos',
  'buildingUtilities',
  'city',
  'housingTypes',
  'listingFloorplans',
  'listingPhotos',
  'ownershipGroups',
  'ownershipTypes',
  'region',
  'suiteTypes',
  'utilities',
  'videos',
  'virtualTours',
];

/**
 * Columns held back from the PUT because the request middleware would rewrite them.
 *
 * `encodeHtmlMiddleware` HTML-encodes `buildingName` and `officeHours` on every `PUT
 * /property-api/properties/{id}`, which is meant to pair with `decodeHtmlMiddleware` on the
 * single-property GET. This panel loads through `properties/_search`, which has no decode rule, so
 * sending them back untouched had them encoded a second time and stored a layer deeper on every
 * assignment save:
 *
 * ```
 * Akam&amp;apos;s Razor -> Akam&amp;amp;apos;s Razor -> ...
 * ```
 *
 * The region write does not need either column, so omitting them keeps this panel from touching text
 * it does not own.
 * @see HTML_ENCODE_CONFIG in src/shared/axios/helpers/const.ts
 */
const middlewareEncodedKeys: string[] = [
  'buildingName',
  'officeHours',
];

/**
 * Works out which of `properties` actually move when a region's checkbox or `Select all` is toggled.
 *
 * `buildings.regionId` is a single scalar, so assigning a property to a region **moves** it out of
 * whichever region held it before — there is no second assignment to add. Unassigning is deliberately
 * limited to properties currently in `regionId`: a property held by a sibling region or by another
 * site's region is never shown as checked here, so clearing it from this region would be destroying an
 * assignment the user did not touch.
 * @param {ICityPageRegionProperty[]} properties - the rows the interaction covers (one row, or every listed row for `Select all`)
 * @param {object} options - the region being toggled and the direction of the toggle
 * @param {number} options.regionId - region the checkbox belongs to
 * @param {boolean} options.isAssigned - true when the box was checked, false when it was cleared
 * @returns {ICityPageRegionAssignmentChange[]} one entry per property whose `regionId` changes; empty when nothing does
 */
export const resolveRegionAssignmentChanges = (
  properties: ICityPageRegionProperty[],
  options: { regionId: number; isAssigned: boolean },
): ICityPageRegionAssignmentChange[] => {
  const { regionId, isAssigned } = options;

  return properties
    .filter((property) => (isAssigned ? property.regionId !== regionId : property.regionId === regionId))
    .map((property) => ({
      propertyId: property.id,
      fromRegionId: property.regionId,
      toRegionId: isAssigned ? regionId : null,
    }));
};

/**
 * Folds newly resolved moves into the pending set, one entry per property.
 *
 * The earliest `fromRegionId` is kept because that is the persisted value; a property toggled twice
 * must still save against where it actually started. A property returned to its original region
 * drops out entirely — there is nothing left to save.
 * @param {ICityPageRegionAssignmentChange[]} pending - moves accumulated so far
 * @param {ICityPageRegionAssignmentChange[]} incoming - moves from the interaction just handled
 */
export const mergeRegionAssignmentChanges = (
  pending: ICityPageRegionAssignmentChange[],
  incoming: ICityPageRegionAssignmentChange[],
): ICityPageRegionAssignmentChange[] => {
  const merged = new Map(pending.map((change) => [change.propertyId, change]));

  incoming.forEach((change) => {
    const existing = merged.get(change.propertyId);
    const fromRegionId = existing ? existing.fromRegionId : change.fromRegionId;

    if (fromRegionId === change.toRegionId) {
      merged.delete(change.propertyId);
      return;
    }

    merged.set(change.propertyId, { ...change, fromRegionId });
  });

  return [...merged.values()];
};

/**
 * Overlays pending moves onto the properties loaded from the API, so counts, checkboxes and the
 * outside-assignment warning all read from one derived list rather than from divergent copies.
 * @param {ICityPageRegionProperty[]} properties - rows as loaded
 * @param {ICityPageRegionAssignmentChange[]} changes - pending moves to apply
 */
export const applyRegionAssignmentChanges = (
  properties: ICityPageRegionProperty[],
  changes: ICityPageRegionAssignmentChange[],
): ICityPageRegionProperty[] => {
  if (changes.length === 0) return properties;

  const changesByPropertyId = new Map(changes.map((change) => [change.propertyId, change]));

  return properties.map((property) => {
    const change = changesByPropertyId.get(property.id);

    return change ? { ...property, regionId: change.toRegionId } : property;
  });
};

/**
 * Splits pending moves into one group per destination, so a flush can be issued per region and a
 * failure against one region leaves the others saved. Unassignments (`toRegionId` null) form their
 * own group.
 * @param {ICityPageRegionAssignmentChange[]} changes - moves to group
 */
export const groupRegionAssignmentChangesByTarget = (
  changes: ICityPageRegionAssignmentChange[],
): ICityPageRegionAssignmentChange[][] => {
  const groups = new Map<number | null, ICityPageRegionAssignmentChange[]>();

  changes.forEach((change) => {
    groups.set(change.toRegionId, [...groups.get(change.toRegionId) ?? [], change]);
  });

  return [...groups.values()];
};

/**
 * Builds the PUT payload for one property's region change: the loaded record minus its relations and
 * minus the columns the request middleware would rewrite, with `regionId` overridden.
 *
 * `properties.save` and `saveMany` are full-object PUTs, so the record carries the columns it was
 * loaded with — the panel's list is loaded without a `select` for exactly that reason. Relations are
 * stripped because the save endpoint expects columns only.
 * @param {IBuildings} property - the property row as loaded, with all of its columns
 * @param {(number | null)} regionId - region to assign, or null to unassign
 */
export const buildRegionAssignmentPayload = (property: IBuildings, regionId: number | null): IBuildings => ({
  ...subsetObject(property, [...propertyRelationKeys, ...middlewareEncodedKeys]),
  regionId,
});
